/* eslint-disable class-methods-use-this */
import {
  AccountAddress,
  ChainId,
  EthereumChainId,
  CosmosChainId,
} from '../../../..';
import {
  UnspecifiedErrorCode,
  CosmosWalletException,
  TransactionException,
  ErrorType,
} from '../../../../exceptions';
import {
  createCosmosSignDocFromTransaction,
  createTxRawFromSigResponse,
} from '../../../../';
import { DirectSignResponse, makeSignDoc } from '@cosmjs/proto-signing';
import { cosmos, InstallError, Cosmos } from '@cosmostation/extension-client';
import { SEND_TRANSACTION_MODE } from '@cosmostation/extension-client/cosmos';
import { TxRaw } from '@routerprotocol/chain-api/cosmos/tx/v1beta1/tx_pb';
import {
  ConcreteWalletStrategy,
  onAccountChangeCallback,
  onChainIdChangeCallback,
} from '../../types';
import BaseConcreteStrategy from './Base';
import { WalletAction, WalletDeviceType } from '../../../types/enums';
import { Msgs } from '../../../../core';
import { TxToSend } from '../../../../tx-ts/ethermint/types';

const ROUTER_CHAIN_NAME = 'router';

export default class Cosmostation extends BaseConcreteStrategy
  implements ConcreteWalletStrategy {
  private provider?: Cosmos;

  constructor(args: { chainId: ChainId }) {
    super(args);
    this.chainId = args.chainId || CosmosChainId.Router;
  }
  simulateTransaction(_signedTx: TxToSend, _nodeUrl: string): Promise<any> {
    throw new Error('Method not implemented.');
  }
  broadcastTransaction(_signedTx: TxToSend, _nodeUrl: string): Promise<any> {
    throw new Error('Method not implemented.');
  }
  simulateSignAndBroadcast(_args: {
    ethChainId: string;
    cosmosChainId: string;
    txMsg: Msgs;
    nodeUrl: string;
    memo?: string;
  }): Promise<any> {
    throw new Error('Method not implemented.');
  }
  onAccountChange?(_callback: onAccountChangeCallback): void {
    throw new Error('Method not implemented.');
  }
  onChainIdChange?(_callback: onChainIdChangeCallback): void {
    throw new Error('Method not implemented.');
  }
  cancelOnChainIdChange?(): void {
    throw new Error('Method not implemented.');
  }
  cancelOnAccountChange?(): void {
    throw new Error('Method not implemented.');
  }
  cancelAllEvents?(): void {
    throw new Error('Method not implemented.');
  }
  disconnect?(): Promise<void> {
    throw new Error('Method not implemented.');
  }

  async getWalletDeviceType(): Promise<WalletDeviceType> {
    return Promise.resolve(WalletDeviceType.Browser);
  }

  async getAddresses(): Promise<string[]> {
    const provider = await this.getProvider();

    try {
      const accounts = await provider.requestAccount(ROUTER_CHAIN_NAME);

      return [accounts.address];
    } catch (e) {
      if ((e as any).code === 4001) {
        throw new CosmosWalletException(
          new Error('The user rejected the request'),
          {
            code: UnspecifiedErrorCode,
            type: ErrorType.WalletError,
            contextModule: WalletAction.GetAccounts,
          }
        );
      }

      throw new CosmosWalletException(new Error((e as any).message), {
        code: UnspecifiedErrorCode,
        type: ErrorType.WalletError,
        contextModule: WalletAction.GetAccounts,
      });
    }
  }

  async confirm(address: AccountAddress): Promise<string> {
    return Promise.resolve(
      `0x${Buffer.from(
        `Confirmation for ${address} at time: ${Date.now()}`
      ).toString('hex')}`
    );
  }

  // eslint-disable-next-line class-methods-use-this
  async sendEthereumTransaction(
    _transaction: unknown,
    _options: {
      address: AccountAddress;
      ethereumChainId: EthereumChainId;
    }
  ): Promise<string> {
    throw new CosmosWalletException(
      new Error(
        'sendEthereumTransaction is not supported. Cosmostation only supports sending cosmos transactions'
      ),
      {
        code: UnspecifiedErrorCode,
        type: ErrorType.WalletError,
        contextModule: WalletAction.SendEthereumTransaction,
      }
    );
  }

  async sendTransaction(
    transaction: DirectSignResponse | TxRaw,
    _options: { address: AccountAddress; chainId: ChainId }
  ): Promise<string> {
    const provider = await this.getProvider();
    const txRaw =
      transaction instanceof TxRaw
        ? transaction
        : createTxRawFromSigResponse(transaction);

    try {
      const response = await provider.sendTransaction(
        ROUTER_CHAIN_NAME,
        txRaw.serializeBinary(),
        SEND_TRANSACTION_MODE.ASYNC
      );

      return response.tx_response.txhash;
    } catch (e) {
      throw new TransactionException(new Error((e as any).message), {
        code: UnspecifiedErrorCode,
        type: ErrorType.ChainError,
        contextModule: WalletAction.SendTransaction,
      });
    }
  }

  /** @deprecated * */
  async signTransaction(
    transaction: {
      txRaw: TxRaw;
      chainId: string;
      accountNumber: number;
    },
    address: AccountAddress
  ) {
    return this.signCosmosTransaction(transaction, address);
  }

  async signCosmosTransaction(
    transaction: {
      txRaw: TxRaw;
      chainId: string;
      accountNumber: number;
    },
    _address: AccountAddress
  ) {
    const { chainId } = this;
    const provider = await this.getProvider();
    const signDoc = createCosmosSignDocFromTransaction(transaction);

    try {
      /* Sign the transaction */
      const signDirectResponse = await provider.signDirect(
        ROUTER_CHAIN_NAME,
        {
          chain_id: chainId,
          body_bytes: signDoc.bodyBytes,
          auth_info_bytes: signDoc.authInfoBytes,
          account_number: signDoc.accountNumber.toString(),
        },
        { fee: true, memo: true }
      );

      return {
        signed: makeSignDoc(
          signDirectResponse.signed_doc.body_bytes,
          signDirectResponse.signed_doc.auth_info_bytes,
          signDirectResponse.signed_doc.chain_id,
          parseInt(signDirectResponse.signed_doc.account_number, 10)
        ),
        signature: {
          signature: signDirectResponse.signature,
        },
      } as DirectSignResponse;
    } catch (e) {
      throw new CosmosWalletException(new Error((e as any).message), {
        code: UnspecifiedErrorCode,
        type: ErrorType.WalletError,
        contextModule: WalletAction.SendTransaction,
      });
    }
  }

  async getPubKey(): Promise<string> {
    const provider = await this.getProvider();

    try {
      const account = await provider.requestAccount(ROUTER_CHAIN_NAME);

      return Buffer.from(account.publicKey).toString('base64');
    } catch (e) {
      if ((e as any).code === 4001) {
        throw new CosmosWalletException(
          new Error('The user rejected the request'),
          {
            code: UnspecifiedErrorCode,
            type: ErrorType.WalletError,
            contextModule: WalletAction.GetAccounts,
          }
        );
      }

      throw new CosmosWalletException(new Error((e as any).message), {
        code: UnspecifiedErrorCode,
        type: ErrorType.WalletError,
        contextModule: WalletAction.GetAccounts,
      });
    }
  }

  async signEip712TypedData(
    _eip712TypedData: string,
    _address: AccountAddress
  ): Promise<string> {
    throw new CosmosWalletException(
      new Error('This wallet does not support signing Ethereum transactions'),
      {
        code: UnspecifiedErrorCode,
        type: ErrorType.WalletError,
        contextModule: WalletAction.SendTransaction,
      }
    );
  }

  async getNetworkId(): Promise<string> {
    throw new CosmosWalletException(
      new Error('getNetworkId is not supported on Cosmostation'),
      {
        code: UnspecifiedErrorCode,
        type: ErrorType.WalletError,
        contextModule: WalletAction.GetNetworkId,
      }
    );
  }

  async getChainId(): Promise<string> {
    throw new CosmosWalletException(
      new Error('getChainId is not supported on Cosmostation'),
      {
        code: UnspecifiedErrorCode,
        type: ErrorType.WalletError,
        contextModule: WalletAction.GetChainId,
      }
    );
  }

  async getEthereumTransactionReceipt(_txHash: string): Promise<string> {
    throw new CosmosWalletException(
      new Error(
        'getEthereumTransactionReceipt is not supported on Cosmostation'
      ),
      {
        code: UnspecifiedErrorCode,
        type: ErrorType.WalletError,
        contextModule: WalletAction.GetEthereumTransactionReceipt,
      }
    );
  }

  private async getProvider(): Promise<Cosmos> {
    if (this.provider) {
      return this.provider;
    }

    try {
      const provider = await cosmos();

      this.provider = provider;

      return provider;
    } catch (e) {
      if (e instanceof InstallError) {
        throw new CosmosWalletException(
          new Error('Please install the Cosmostation extension'),
          {
            code: UnspecifiedErrorCode,
            type: ErrorType.WalletNotInstalledError,
          }
        );
      }

      throw new CosmosWalletException(new Error((e as any).message), {
        code: UnspecifiedErrorCode,
        type: ErrorType.WalletError,
      });
    }
  }
}
